import React, { useContext, useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Dropdown, Collapse } from "antd";
import styled from "styled-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import unitModel from "../../lib/firebase/unitModel";
import { propertyKeys } from "../../lib/queryKeys";
import { FREE_TIER, isFreeTierUser } from "../../lib/pricing";
import { AuthContext } from "../../context/AuthProvider";
import { StyledLink } from "../../styles/CommonElements";
import { firebaseAuth } from "../../helpers/authentication";
import { BackLocationsContext } from "../../context/BackLocationsProvider";
import SettingsModal from "../profile/SettingsModal/SettingsModal";
import EditModal from "../profile/EditModal";
import Logo from "./Logo";
import {
  DIALOG_NAME,
  useDialogSearchParam,
  useOpenParamDialog,
} from "../../helpers/useDialogSearchParam";
import { device, useBreakpoints } from "../../lib/breakpoints";
import { trackEvent } from "../../helpers/mixpanel";
import mixpanel from "mixpanel-browser";
import { APP_NAME } from "../../helpers/constants";
import { showHeaderUi } from "../../helpers/general";
import Button from "../Button";
import IntakeModal from "../IntakeModal";
import {
  BulbOutlined,
  HomeOutlined,
  UserOutlined,
  SettingOutlined,
  ToolOutlined,
  FormOutlined,
  IdcardOutlined,
  LogoutOutlined,
  MessageOutlined,
  GiftOutlined,
  GlobalOutlined,
  WalletOutlined,
  RiseOutlined,
  FileTextOutlined,
  LockOutlined,
  CommentOutlined,
} from "@ant-design/icons";
import { userKeys } from "../../lib/queryKeys";
import { shouldShowScaleUpgradeForFullOs } from "../../helpers/accountProduct";
import ScaleUpgradeModal from "../ScaleUpgradeModal";
import NotificationBell from "./NotificationBell";

const PROFILE_ICON = "/icons/profile.svg";
const MENU_ICON = "/icons/hamburger.svg";

// use shared helper showHeaderUi from helpers/general

export default function NavBar() {
  const { user, userInfo, isOpsChatOnlyUser } = useContext(AuthContext);
  const { clearBackLocations } = useContext(BackLocationsContext);
  const { isMobile, isDesktop } = useBreakpoints();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [intakeModalPlan, setIntakeModalPlan] = useState("starter");
  const queryClient = useQueryClient();
  const [scaleUpgradeModalOpen, setScaleUpgradeModalOpen] = useState(false);

  // Modal dialog hooks
  const [isEditParamSet, removeEditParam] = useDialogSearchParam(
    DIALOG_NAME.edit
  );
  const [isSettingsParamSet, removeSettingsParam] = useDialogSearchParam(
    DIALOG_NAME.settings
  );
  const openLogin = useOpenParamDialog(DIALOG_NAME.login);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest(".mobile-nav")) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const onLogout = async () => {
    try {
      // Close mobile menu when logging out
      setMobileMenuOpen(false);

      await firebaseAuth.signOut();

      // Only clear user-related queries, not entire cache
      queryClient.removeQueries({ queryKey: userKeys.user() });

      trackEvent("User Logged Out", {
        from_page: location.pathname,
        device_type: isDesktop ? "desktop" : "mobile",
      });
      clearBackLocations();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleNavigation = (path) => {
    clearBackLocations();
    // Close mobile menu when navigating
    setMobileMenuOpen(false);
    mixpanel.track("Navigation Click", {
      destination: path,
      from_page: location.pathname,
      is_mobile: !isDesktop,
      is_tablet: !isDesktop,
    });
  };

  const handleLoginClick = () => {
    trackEvent("Login Button Click", {
      from_page: location.pathname,
      device_type: isDesktop ? "desktop" : "mobile",
    });

    // Close mobile menu when clicking login
    setMobileMenuOpen(false);

    openLogin();
  };

  const handleFreeTrialClick = () => {
    trackEvent("Free Trial Button Click", {
      from_page: location.pathname,
      device_type: isDesktop ? "desktop" : "mobile",
      context: "navbar_get_started",
    });

    setMobileMenuOpen(false);
    setIntakeModalPlan("starter");
    setShowIntakeModal(true);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navOffset = window.innerWidth <= 768 ? 76 : 88;
      const y =
        element.getBoundingClientRect().top + window.scrollY - navOffset;
      window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
    }
  };

  const goHomeSection = (sectionId) => {
    setMobileMenuOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${sectionId}`);
      return;
    }
    scrollToSection(sectionId);
  };

  const handlePricingClick = () => goHomeSection("pricing");
  const handlePlatformClick = () => goHomeSection("product-features");

  const handleLeoDemoClick = () => {
    trackEvent("Navbar Demo Click", {
      from_page: location.pathname,
      device_type: isDesktop ? "desktop" : "mobile",
      target: "leo-demo-chat",
    });
    goHomeSection("leo-demo-chat");
  };

  /**
   * 3-Unit Free Gateway wedge: only mounted for users whose stored plan is
   * explicitly "free" so legacy accounts without a plan string are untouched.
   * Door count is read from the same Firestore query key as Dashboard/Units so
   * the pill stays in sync when a unit is created/deleted elsewhere.
   * Must live above any early return to satisfy rules-of-hooks.
   */
  const isFreeUser = isFreeTierUser(userInfo);
  const { data: freeTierUnits } = useQuery({
    queryKey: propertyKeys.units(user?.uid),
    queryFn: () => unitModel.getByOwner(user.uid),
    enabled: Boolean(isFreeUser && user?.uid),
    staleTime: 30_000,
  });

  // Don't show header on certain pages
  if (!showHeaderUi(location)) {
    return null;
  }

  const lockFullOs = shouldShowScaleUpgradeForFullOs(userInfo);

  const freeTierUnitCount = Array.isArray(freeTierUnits)
    ? freeTierUnits.length
    : 0;
  const freeTierCap = FREE_TIER.unitCap;
  const freeTierAtCap = freeTierUnitCount >= freeTierCap;
  const handleFreeUpgrade = () => {
    trackEvent("free_gateway_upgrade_clicked", {
      source: "navbar_progress_pill",
      unit_count: freeTierUnitCount,
      at_cap: freeTierAtCap,
    });
    navigate("/profile#subscription-billing");
  };

  // Portfolio dropdown items
  const propertyManagementItems = [
    {
      key: "units",
      label: (
        <Link
          to="/units"
          onClick={() => handleNavigation("/units")}
          className={location.pathname === "/units" ? "active" : ""}
        >
          <HomeOutlined /> Doors
        </Link>
      ),
    },
    {
      key: "tenants",
      label: (
        <Link
          to="/tenants"
          onClick={() => handleNavigation("/tenants")}
          className={location.pathname === "/tenants" ? "active" : ""}
        >
          <UserOutlined /> Tenants
        </Link>
      ),
    },
    {
      key: "analyzer",
      label: (
        <Link
          to="/analyzer"
          onClick={() => handleNavigation("/analyzer")}
          className={location.pathname === "/analyzer" ? "active" : ""}
        >
          <RiseOutlined /> Deal Analyzer
        </Link>
      ),
    },
    {
      key: "communication",
      label: (
        <Link
          to="/communication"
          onClick={() => handleNavigation("/communication")}
          className={location.pathname === "/communication" ? "active" : ""}
        >
          <MessageOutlined /> Messages
        </Link>
      ),
    },
    {
      key: "leases",
      label: (
        <Link
          to="/leases"
          onClick={() => handleNavigation("/leases")}
          className={location.pathname === "/leases" ? "active" : ""}
        >
          <FileTextOutlined /> Leases
        </Link>
      ),
    },
    {
      key: "repairs",
      label: (
        <Link
          to="/repairs"
          onClick={() => handleNavigation("/repairs")}
          className={location.pathname === "/repairs" ? "active" : ""}
        >
          <ToolOutlined /> Repairs
        </Link>
      ),
    },
    {
      key: "expenses",
      label: (
        <Link
          to="/expenses"
          onClick={() => handleNavigation("/expenses")}
          className={location.pathname === "/expenses" ? "active" : ""}
        >
          <WalletOutlined /> Expenses
        </Link>
      ),
    },
    {
      key: "listings",
      label: (
        <Link
          to="/listings"
          onClick={() => handleNavigation("/listings")}
          className={location.pathname === "/listings" ? "active" : ""}
        >
          <GlobalOutlined /> Listings
        </Link>
      ),
    },
    {
      key: "lease-ups",
      label: (
        <Link
          to="/lease-ups"
          onClick={() => handleNavigation("/lease-ups")}
          className={
            location.pathname === "/lease-ups" ||
            location.pathname.startsWith("/lease-ups/")
              ? "active"
              : ""
          }
        >
          <FileTextOutlined /> Lease-Ups
        </Link>
      ),
    },
  ];

  // Profile dropdown items (full product: Settings + Invite + Profile; OpsChat: Profile only)
  const profileItems = [
    ...(!isOpsChatOnlyUser
      ? [
          {
            key: "settings",
            label: (
              <Link
                to="/settings"
                onClick={() => handleNavigation("/settings")}
                className={location.pathname === "/settings" ? "active" : ""}
              >
                <SettingOutlined /> Settings
              </Link>
            ),
          },
          {
            key: "offers",
            label: (
              <Link
                to="/offers"
                onClick={() => handleNavigation("/offers")}
                className={location.pathname === "/offers" ? "active" : ""}
              >
                <GiftOutlined /> Invite
              </Link>
            ),
          },
        ]
      : []),
    {
      key: "profile",
      label: (
        <Link
          to="/profile"
          onClick={() => handleNavigation("/profile")}
          className={location.pathname === "/profile" ? "active" : ""}
        >
          <IdcardOutlined /> Profile
        </Link>
      ),
    },
    {
      type: "divider",
    },
    {
      key: "logout",
      label: (
        <Link onClick={onLogout} to="/">
          <LogoutOutlined /> Logout
        </Link>
      ),
    },
  ];

  return (
    <Header $mobileMenuElevated={mobileMenuOpen && !isDesktop}>
      <GlobalDropdownStyles />
      <HeaderContent>
        <LogoSection>
          <Logo color="" />
        </LogoSection>

        {!isDesktop ? (
          <MobileNav className="mobile-nav">
            {user ? <NotificationBell variant="mobile" /> : null}
            <MobileMenuButton
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <img src={MENU_ICON} alt="Menu" />
            </MobileMenuButton>

            {mobileMenuOpen && (
              <MobileMenu>
                {user && isOpsChatOnlyUser ? (
                  <>
                    <MobileSectionHeader>OpsChat</MobileSectionHeader>
                    <MobileMenuItem>
                      <Link
                        to="/settings"
                        onClick={() => handleNavigation("/settings")}
                        className={
                          location.pathname === "/settings" ? "active" : ""
                        }
                      >
                        <SettingOutlined /> Setup &amp; account
                      </Link>
                    </MobileMenuItem>
                    <MobileMenuItem>
                      <Link
                        to="/units"
                        onClick={() => handleNavigation("/units")}
                        className={
                          location.pathname === "/units" ? "active" : ""
                        }
                      >
                        <HomeOutlined /> Doors
                      </Link>
                    </MobileMenuItem>
                    <MobileMenuItem>
                      <Link
                        to="/communication"
                        onClick={() => handleNavigation("/communication")}
                        className={
                          location.pathname.startsWith("/communication")
                            ? "active"
                            : ""
                        }
                      >
                        <CommentOutlined /> Conversations
                      </Link>
                    </MobileMenuItem>
                    <MobileMenuItem>
                      <Link
                        to="/profile"
                        onClick={() => handleNavigation("/profile")}
                      >
                        <IdcardOutlined /> Profile
                      </Link>
                    </MobileMenuItem>
                    <MobileMenuItem>
                      <LogoutLink onClick={onLogout}>
                        <LogoutOutlined /> Logout
                      </LogoutLink>
                    </MobileMenuItem>
                  </>
                ) : user ? (
                  <>
                    <MobileMenuItem>
                      <Link
                        to="/dashboard"
                        onClick={() => handleNavigation("/dashboard")}
                        className={
                          location.pathname === "/dashboard" ? "active" : ""
                        }
                      >
                        Dashboard
                      </Link>
                    </MobileMenuItem>
                    <MobileMenuItem>
                      {lockFullOs ? (
                        <MobileLockNavButton
                          type="button"
                          onClick={() => setScaleUpgradeModalOpen(true)}
                        >
                          <LockOutlined style={{ marginRight: 6 }} />
                          Payments
                        </MobileLockNavButton>
                      ) : (
                        <Link
                          to="/rent"
                          onClick={() => handleNavigation("/rent")}
                          className={
                            location.pathname === "/rent" ? "active" : ""
                          }
                        >
                          Payments
                        </Link>
                      )}
                    </MobileMenuItem>
                    <MobileNavCollapse
                      accordion={false}
                      defaultActiveKey={[]}
                      expandIconPosition="end"
                      items={[
                        {
                          key: "portfolio",
                          label: <strong>Portfolio</strong>,
                          children: (
                            <>
                              <MobileMenuItem>
                                <Link
                                  to="/units"
                                  onClick={() => handleNavigation("/units")}
                                  className={
                                    location.pathname === "/units"
                                      ? "active"
                                      : ""
                                  }
                                >
                                  <HomeOutlined /> Doors
                                </Link>
                              </MobileMenuItem>
                              <MobileMenuItem>
                                <Link
                                  to="/tenants"
                                  onClick={() => handleNavigation("/tenants")}
                                  className={
                                    location.pathname === "/tenants"
                                      ? "active"
                                      : ""
                                  }
                                >
                                  <UserOutlined /> Tenants
                                </Link>
                              </MobileMenuItem>
                              <MobileMenuItem>
                                <Link
                                  to="/analyzer"
                                  onClick={() => handleNavigation("/analyzer")}
                                  className={
                                    location.pathname === "/analyzer"
                                      ? "active"
                                      : ""
                                  }
                                >
                                  <RiseOutlined /> Deal Analyzer
                                </Link>
                              </MobileMenuItem>
                              <MobileMenuItem>
                                <Link
                                  to="/listings"
                                  onClick={() => handleNavigation("/listings")}
                                  className={
                                    location.pathname === "/listings"
                                      ? "active"
                                      : ""
                                  }
                                >
                                  <GlobalOutlined /> Listings
                                </Link>
                              </MobileMenuItem>
                              <MobileMenuItem>
                                <Link
                                  to="/leases"
                                  onClick={() => handleNavigation("/leases")}
                                  className={
                                    location.pathname === "/leases"
                                      ? "active"
                                      : ""
                                  }
                                >
                                  <FileTextOutlined /> Leases
                                </Link>
                              </MobileMenuItem>
                              <MobileMenuItem>
                                <Link
                                  to="/lease-ups"
                                  onClick={() =>
                                    handleNavigation("/lease-ups")
                                  }
                                  className={
                                    location.pathname === "/lease-ups" ||
                                    location.pathname.startsWith("/lease-ups/")
                                      ? "active"
                                      : ""
                                  }
                                >
                                  <FileTextOutlined /> Lease-Ups
                                </Link>
                              </MobileMenuItem>
                            </>
                          ),
                        },
                        {
                          key: "operations",
                          label: <strong>Operations</strong>,
                          children: (
                            <>
                              <MobileMenuItem>
                                <Link
                                  to="/leo"
                                  onClick={() => handleNavigation("/leo")}
                                  className={
                                    location.pathname === "/leo"
                                      ? "active"
                                      : ""
                                  }
                                >
                                  Leo AI
                                </Link>
                              </MobileMenuItem>
                              <MobileMenuItem>
                                <Link
                                  to="/communication"
                                  onClick={() =>
                                    handleNavigation("/communication")
                                  }
                                  className={
                                    location.pathname === "/communication"
                                      ? "active"
                                      : ""
                                  }
                                >
                                  <MessageOutlined /> Messages
                                </Link>
                              </MobileMenuItem>
                              <MobileMenuItem>
                                <Link
                                  to="/repairs"
                                  onClick={() => handleNavigation("/repairs")}
                                  className={
                                    location.pathname === "/repairs"
                                      ? "active"
                                      : ""
                                  }
                                >
                                  <ToolOutlined /> Repairs
                                </Link>
                              </MobileMenuItem>
                              <MobileMenuItem>
                                <Link
                                  to="/expenses"
                                  onClick={() => handleNavigation("/expenses")}
                                  className={
                                    location.pathname === "/expenses"
                                      ? "active"
                                      : ""
                                  }
                                >
                                  <WalletOutlined /> Expenses
                                </Link>
                              </MobileMenuItem>
                            </>
                          ),
                        },
                        {
                          key: "account",
                          label: <strong>Account</strong>,
                          children: (
                            <>
                              <MobileMenuItem>
                                <Link
                                  to="/offers"
                                  onClick={() => handleNavigation("/offers")}
                                  className={
                                    location.pathname === "/offers"
                                      ? "active"
                                      : ""
                                  }
                                >
                                  <GiftOutlined /> Invite
                                </Link>
                              </MobileMenuItem>
                              <MobileMenuItem>
                                <Link
                                  to="/settings"
                                  onClick={() => handleNavigation("/settings")}
                                  className={
                                    location.pathname === "/settings"
                                      ? "active"
                                      : ""
                                  }
                                >
                                  <SettingOutlined /> Settings
                                </Link>
                              </MobileMenuItem>
                              <MobileMenuItem>
                                <Link
                                  to="/profile"
                                  onClick={() => handleNavigation("/profile")}
                                >
                                  <IdcardOutlined /> Profile
                                </Link>
                              </MobileMenuItem>
                              <MobileMenuItem>
                                <LogoutLink onClick={onLogout}>
                                  <LogoutOutlined /> Logout
                                </LogoutLink>
                              </MobileMenuItem>
                            </>
                          ),
                        },
                      ]}
                    />
                  </>
                ) : (
                  <>
                    <MobileMenuItem>
                      <MobileAuthButton type="button" onClick={handlePricingClick}>
                        Pricing
                      </MobileAuthButton>
                    </MobileMenuItem>
                    <MobileMenuItem>
                      <MobileAuthButton
                        type="button"
                        onClick={handlePlatformClick}
                      >
                        Platform
                      </MobileAuthButton>
                    </MobileMenuItem>
                    <MobileMenuItem>
                      <MobileAuthButton
                        type="button"
                        onClick={handleLeoDemoClick}
                      >
                        Demo
                      </MobileAuthButton>
                    </MobileMenuItem>
                    <MobileMenuItem>
                      <Link
                        to="/contact"
                        onClick={() => handleNavigation("/contact")}
                        className={
                          location.pathname === "/contact" ? "active" : ""
                        }
                      >
                        Contact
                      </Link>
                    </MobileMenuItem>
                    <MobileMenuItem>
                      <MobileAuthLink onClick={handleLoginClick}>
                        Login
                      </MobileAuthLink>
                    </MobileMenuItem>
                    <MobileMenuItem>
                      <MobileAuthLink onClick={handleFreeTrialClick}>
                        Get Started
                      </MobileAuthLink>
                    </MobileMenuItem>
                  </>
                )}
              </MobileMenu>
            )}
          </MobileNav>
        ) : (
          <DesktopNav>
            {user && isOpsChatOnlyUser ? (
              <>
                <NavLinks>
                  <OpsChatBrandBadge>OpsChat</OpsChatBrandBadge>
                  <NavLink>
                    <Link
                      to="/settings"
                      onClick={() => handleNavigation("/settings")}
                      className={
                        location.pathname === "/settings" ? "active" : ""
                      }
                    >
                      <SettingOutlined style={{ marginRight: 6 }} />
                      Setup
                    </Link>
                  </NavLink>
                  <NavLink>
                    <Link
                      to="/units"
                      onClick={() => handleNavigation("/units")}
                      className={
                        location.pathname === "/units" ? "active" : ""
                      }
                    >
                      <HomeOutlined style={{ marginRight: 6 }} />
                      Doors
                    </Link>
                  </NavLink>
                  <NavLink>
                    <Link
                      to="/communication"
                      onClick={() => handleNavigation("/communication")}
                      className={
                        location.pathname.startsWith("/communication")
                          ? "active"
                          : ""
                      }
                    >
                      <CommentOutlined style={{ marginRight: 6 }} />
                      Conversations
                    </Link>
                  </NavLink>
                </NavLinks>
                <UserSection>
                  <NotificationBell variant="desktop" />
                  <Dropdown
                    menu={{ items: profileItems }}
                    placement="bottomRight"
                    trigger={["click"]}
                    onOpenChange={(open) => {
                      if (!open) {
                        setMobileMenuOpen(false);
                      }
                    }}
                  >
                    <UserButton>
                      <UserAvatar>
                        {userInfo?.profileImage ? (
                          <img src={userInfo.profileImage} alt="Profile" />
                        ) : (
                          <img src={PROFILE_ICON} alt="Profile" />
                        )}
                      </UserAvatar>
                    </UserButton>
                  </Dropdown>
                </UserSection>
              </>
            ) : user ? (
              <>
                <NavLinks>
                  <NavLink>
                    <Link
                      to="/dashboard"
                      onClick={() => handleNavigation("/dashboard")}
                      className={
                        location.pathname === "/dashboard" ? "active" : ""
                      }
                    >
                      Dashboard
                    </Link>
                  </NavLink>
                  <NavLink>
                    {lockFullOs ? (
                      <NavScrollLink
                        type="button"
                        onClick={() => setScaleUpgradeModalOpen(true)}
                      >
                        <LockOutlined style={{ marginRight: 6 }} />
                        Payments
                      </NavScrollLink>
                    ) : (
                      <Link
                        to="/rent"
                        onClick={() => handleNavigation("/rent")}
                        className={
                          location.pathname === "/rent" ? "active" : ""
                        }
                      >
                        Payments
                      </Link>
                    )}
                  </NavLink>
                  <NavLink>
                    <Dropdown
                      menu={{ items: propertyManagementItems }}
                      placement="bottomLeft"
                      trigger={["hover"]}
                    >
                      <DropdownButton>
                        Portfolio
                        <DropdownArrow>▼</DropdownArrow>
                      </DropdownButton>
                    </Dropdown>
                  </NavLink>
                  <NavLink>
                    <Link
                      to="/leo"
                      onClick={() => handleNavigation("/leo")}
                      className={location.pathname === "/leo" ? "active" : ""}
                    >
                      Leo AI
                    </Link>
                  </NavLink>
                </NavLinks>

                {isFreeUser && (
                  <FreeGatewayWedge
                    count={freeTierUnitCount}
                    cap={freeTierCap}
                    atCap={freeTierAtCap}
                    onUpgrade={handleFreeUpgrade}
                  />
                )}

                <UserSection>
                  <NotificationBell variant="desktop" />
                  <Dropdown
                    menu={{ items: profileItems }}
                    placement="bottomRight"
                    trigger={["click"]}
                    onOpenChange={(open) => {
                      if (!open) {
                        // Close mobile menu when dropdown closes
                        setMobileMenuOpen(false);
                      }
                    }}
                  >
                    <UserButton>
                      <UserAvatar>
                        {userInfo?.profileImage ? (
                          <img src={userInfo.profileImage} alt="Profile" />
                        ) : (
                          <img src={PROFILE_ICON} alt="Profile" />
                        )}
                      </UserAvatar>
                    </UserButton>
                  </Dropdown>
                </UserSection>
              </>
            ) : (
              <>
                <NavLinks>
                  <NavLink>
                    <NavScrollLink type="button" onClick={handlePricingClick}>
                      Pricing
                    </NavScrollLink>
                  </NavLink>
                  <NavLink>
                    <NavScrollLink
                      type="button"
                      onClick={handlePlatformClick}
                    >
                      Platform
                    </NavScrollLink>
                  </NavLink>
                  <NavLink>
                    <NavScrollLink type="button" onClick={handleLeoDemoClick}>
                      Demo
                    </NavScrollLink>
                  </NavLink>
                </NavLinks>

                <AuthButtons>
                  <LoginButton onClick={handleLoginClick}>Login</LoginButton>
                  <SignupButton onClick={handleFreeTrialClick}>
                    Get Started
                  </SignupButton>
                </AuthButtons>
              </>
            )}
          </DesktopNav>
        )}
      </HeaderContent>

      {/* Modals */}
      {isEditParamSet && (
        <EditModal isOpen={isEditParamSet} onClose={removeEditParam} />
      )}

      {isSettingsParamSet && (
        <SettingsModal
          isOpen={isSettingsParamSet}
          onClose={removeSettingsParam}
        />
      )}

      <IntakeModal
        isOpen={showIntakeModal}
        onClose={() => {
          setShowIntakeModal(false);
          setIntakeModalPlan("starter");
        }}
        selectedPlan={intakeModalPlan}
        onSubmit={(profileData) => {
          console.log("Profile data submitted:", profileData);
          setShowIntakeModal(false);
        }}
      />

      <ScaleUpgradeModal
        open={scaleUpgradeModalOpen}
        onClose={() => setScaleUpgradeModalOpen(false)}
      />
    </Header>
  );
}

// Styled Components
/* Onboarding / Leo widgets use z-index ~1e7; header must lift its stacking context when
   the mobile sheet is open or the menu stays visually under those floats. Modals use ~1e4. */
const Z_NAV_MOBILE_MENU_ELEVATED = 10000050;

const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${(p) =>
    p.$mobileMenuElevated ? Z_NAV_MOBILE_MENU_ELEVATED : 1000};
  background: var(--color-bg-secondary);
  border-bottom: 1px solid var(--color-border-light);

  @media ${device.mobile}, ${device.tablet} {
    z-index: ${(p) =>
      p.$mobileMenuElevated ? Z_NAV_MOBILE_MENU_ELEVATED : 1000} !important;
  }
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: var(--navbar-height);

  @media ${device.mobile} {
    padding: 0 1rem;
    height: var(--navbar-height-mobile);
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
`;

const DesktopNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media ${device.desktop} {
    display: flex;
  }

  @media ${device.mobile}, ${device.tablet} {
    display: none;
  }
`;

const MobileNav = styled.nav`
  display: none;
  position: relative;
  z-index: 1001 !important; /* Just above navbar (1000) */

  @media ${device.mobile}, ${device.tablet} {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    z-index: 1001 !important;
  }

  @media ${device.desktop} {
    display: none;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2.5rem;
`;

const NavLink = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  a,
  button {
    color: var(--color-text-primary);
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s ease;
    position: relative;
    padding: 0;
    white-space: nowrap;

    &:hover {
      color: var(--color-primary);
    }

    &.active {
      color: var(--color-primary);

      &::after {
        content: "";
        position: absolute;
        bottom: -8px;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--color-primary);
        border-radius: 1px;
      }
    }
  }
`;

const NavScrollLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--color-text-primary);
  text-decoration: none;
  font-weight: 500;
  font-size: inherit;
  font-family: inherit;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    color: var(--color-primary);
  }
`;

const MobileLockNavButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  width: 100%;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font: inherit;
  display: flex;
  align-items: center;
`;

const DropdownButton = styled.button`
  background: transparent;
  border: none;
  color: var(--color-text-primary);
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  border-radius: 6px;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.35rem;

  &:hover {
    color: var(--color-primary);
  }
`;

const DropdownArrow = styled.span`
  font-size: 0.75rem;
  transition: transform 0.2s ease;

  .ant-dropdown-open & {
    transform: rotate(180deg);
  }
`;

const AuthButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const LoginButton = styled.button`
  background: transparent;
  border: 1px solid var(--color-primary);
  color: var(--color-primary);
  padding: 0.5rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-primary);
    color: white;
  }
`;

const SignupButton = styled.button`
  background: var(--color-primary);
  border: 1px solid var(--color-primary);
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-primary-dark);
    border-color: var(--color-primary-dark);
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;

  /* Keep bell + profile on the same vertical center line */
  > * {
    display: inline-flex;
    align-items: center;
  }
`;

/**
 * 3-Unit Free Gateway indicator.
 * Shows `Doors: X/3 used` progress for free-tier users. At 3/3 the pill turns
 * Success Mint green and surfaces the "Upgrade plan" CTA.
 */
const FreeWedgeContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  margin-right: 0.75rem;
  padding: 0.35rem 0.5rem 0.35rem 0.75rem;
  border-radius: 999px;
  background: ${(p) =>
    p.$atCap
      ? "rgba(134, 228, 157, 0.18)"
      : "var(--color-bg-tertiary, #f3f4f6)"};
  border: 1px solid
    ${(p) => (p.$atCap ? "#86E49D" : "var(--color-border-light)")};
  font-family: var(--font-secondary);
  transition: background 0.25s ease, border-color 0.25s ease;
`;

const FreeWedgeCopy = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.15;
`;

const FreeWedgeLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${(p) =>
    p.$atCap ? "var(--color-primary-dark)" : "var(--color-text-light)"};
`;

const FreeWedgeCount = styled.span`
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${(p) =>
    p.$atCap ? "var(--color-primary-dark)" : "var(--color-text)"};
`;

const FreeWedgeBar = styled.div`
  position: relative;
  width: 64px;
  height: 6px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.08);
  overflow: hidden;

  &:after {
    content: "";
    position: absolute;
    inset: 0;
    width: ${(p) => `${Math.min(100, (p.$pct || 0) * 100)}%`};
    background: ${(p) =>
      p.$atCap ? "#2CB76B" : "var(--color-primary, #2c5f2d)"};
    transition: width 0.3s ease, background 0.3s ease;
  }
`;

const FreeWedgeUpgradeBtn = styled.button`
  all: unset;
  cursor: pointer;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  background: var(--color-primary);
  color: #fff;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(44, 95, 45, 0.28);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary-dark);
    outline-offset: 2px;
  }
`;

function FreeGatewayWedge({ count, cap, atCap, onUpgrade }) {
  const pct = cap > 0 ? count / cap : 0;
  return (
    <FreeWedgeContainer $atCap={atCap} aria-label="Free plan usage">
      <FreeWedgeCopy>
        <FreeWedgeLabel $atCap={atCap}>
          {atCap ? "Free · Cap reached" : "Free plan"}
        </FreeWedgeLabel>
        <FreeWedgeCount $atCap={atCap}>
          Doors: {count}/{cap} used
        </FreeWedgeCount>
      </FreeWedgeCopy>
      <FreeWedgeBar $pct={pct} $atCap={atCap} aria-hidden="true" />
      {atCap && (
        <FreeWedgeUpgradeBtn type="button" onClick={onUpgrade}>
          Upgrade plan
        </FreeWedgeUpgradeBtn>
      )}
    </FreeWedgeContainer>
  );
}

const UserButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--color-bg-tertiary);
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const LogoutButton = styled.button`
  background: transparent;
  border: none;
  color: var(--color-error);
  cursor: pointer;
  padding: 0.5rem 1rem;
  width: 100%;
  text-align: left;
  font-weight: 500;

  &:hover {
    background: var(--color-bg-tertiary);
  }

  /* Override Ant Design dropdown styling for desktop */
  @media ${device.desktop} {
    text-align: left !important;
    justify-content: flex-start !important;
    display: flex !important;
  }
`;

const MobileMenuButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;

  img {
    width: 24px;
    height: 24px;
  }
`;

const MobileMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--color-bg-secondary);
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: 8px;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.14),
    0 2px 8px rgba(0, 0, 0, 0.08);
  min-width: 200px;
  max-height: 80vh;
  overflow-y: auto;
  z-index: 1002 !important; /* Above MobileNav (1001) but below modals (10000+) */

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--color-bg-tertiary);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-border-light);
    border-radius: 3px;

    &:hover {
      background: var(--color-text-light);
    }
  }
`;

const MobileSectionHeader = styled.div`
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-light);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: var(--color-bg-tertiary);
  border-top: 1px solid var(--color-border-light);
  border-bottom: 1px solid var(--color-border-light);
`;

const MobileNavCollapse = styled(Collapse)`
  background: transparent !important;
  border: none !important;

  .ant-collapse-item {
    border-bottom: 1px solid var(--color-border-light) !important;

    &:last-child {
      border-bottom: none !important;
    }
  }

  .ant-collapse-header {
    padding: 0.75rem 1rem !important;
    font-weight: 700 !important;
    font-size: 0.95rem !important;
    color: var(--color-text-primary) !important;
    text-transform: none !important;
    letter-spacing: 0 !important;
    align-items: center !important;
    justify-content: space-between !important;
    text-align: left !important;
  }

  .ant-collapse-header-text,
  .ant-collapse-header-text strong {
    font-weight: 700 !important;
    font-size: 0.95rem !important;
    color: var(--color-text-primary) !important;
    flex: 1 !important;
    text-align: left !important;
  }

  .ant-collapse-content-box {
    padding: 0 !important;
  }

  .ant-collapse-arrow {
    color: var(--color-text-light) !important;
  }
`;

const MobileMenuItem = styled.div`
  position: relative;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--color-border-light);
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  a,
  button {
    color: var(--color-text-primary);
    text-decoration: none;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.5rem;
    transition: all 0.2s ease;
    position: relative;

    &.active {
      color: var(--color-primary);

      &::after {
        content: "";
        position: absolute;
        bottom: -4px;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--color-primary);
        border-radius: 1px;
      }
    }
  }
`;

const MobileAuthLink = styled(Link)`
  color: var(--color-primary);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;

  &:hover {
    color: var(--color-primary-dark);
  }
`;

const MobileAuthButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--color-primary);
  text-decoration: none;
  font-weight: 500;
  font-size: inherit;
  font-family: inherit;
  transition: color 0.2s ease;

  &:hover {
    color: var(--color-primary-dark);
  }
`;

const LogoutLink = styled(Link)`
  color: var(--color-error);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;

  &:hover {
    color: var(--color-error-dark);
  }
`;

const MobileDropdownButton = styled.button`
  background: transparent;
  border: none;
  color: var(--color-text-primary);
  text-decoration: none;
  font-weight: 500;
  display: block;
  width: 100%;
  text-align: left;
  padding: 0;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: var(--color-primary);
  }
`;

const OpsChatBrandBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-primary);
  background: rgba(44, 95, 45, 0.12);
  border: 1px solid rgba(44, 95, 45, 0.25);
  font-family: var(--font-primary);
  margin-right: 0.5rem;
`;

// Global styles to override Ant Design dropdown
const GlobalDropdownStyles = styled.div`
  .ant-dropdown-menu-item {
    text-align: left !important;
    justify-content: flex-start !important;
  }

  .ant-dropdown-menu-item button {
    text-align: left !important;
    justify-content: flex-start !important;
    width: 100% !important;
  }

  .ant-dropdown-menu-item a {
    display: flex !important;
    align-items: center !important;
    gap: 0.5rem !important;
    padding: 0.5rem 1rem !important;
    color: var(--color-text-primary) !important;
    text-decoration: none !important;
    transition: all 0.2s ease !important;

    &:hover {
      color: var(--color-primary) !important;
      background: var(--color-bg-tertiary) !important;
    }

    &.active {
      color: var(--color-primary) !important;
      background: var(--color-bg-tertiary) !important;
    }
  }
`;
